import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  listCatalogAttributeRecords,
  syncCatalogAttributesFromApi,
  type CatalogAttributeRecord,
} from "@/lib/catalog/catalogAttributesStore";
import {
  listLinksForCategory,
  setCategoryAttributeLinksViaApi,
  syncLinksForCategoryFromApi,
} from "@/lib/catalog/categoryAttributeLinksStore";
import type { ID } from "@/lib/catalog/types";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import { ArrowDown, ArrowUp, Check, Loader2, Search, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  categoryId: ID | null;
  categoryName?: string | null;
  onSaved: () => void | Promise<void>;
};

function formatApiErrorMessage(error: unknown): string {
  if (isAxiosError(error) && error.response?.data) {
    const d = error.response.data as { message?: string; errors?: Record<string, string[]> };
    if (typeof d.message === "string" && d.message.trim()) return d.message;
    if (d.errors) {
      const first = Object.values(d.errors).flat().find((m) => m?.length);
      if (first) return first;
    }
  }
  return "Unable to save category attributes. Please try again.";
}

export function AssignCategoryAttributesDialog({
  open,
  onOpenChange,
  categoryId,
  categoryName,
  onSaved,
}: Props) {
  const [attributes, setAttributes] = useState<CatalogAttributeRecord[]>(() => listCatalogAttributeRecords());
  const [orderedAttributeIds, setOrderedAttributeIds] = useState<ID[]>([]);
  const [attrPickerOpen, setAttrPickerOpen] = useState(false);
  const [attrSearch, setAttrSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open || !categoryId) return;
    let cancelled = false;
    setLoading(true);
    setAttrSearch("");
    setAttrPickerOpen(false);

    void (async () => {
      try {
        await Promise.all([syncCatalogAttributesFromApi().catch(() => null), syncLinksForCategoryFromApi(categoryId)]);
        if (cancelled) return;
        setAttributes(listCatalogAttributeRecords());
        setOrderedAttributeIds(listLinksForCategory(categoryId).map((l) => l.attributeId));
      } catch (error) {
        if (cancelled) return;
        if (isAxiosError(error) && error.response?.status === 403) {
          toast.error("You don’t have permission to load category fields.");
          onOpenChange(false);
          return;
        }
        toast.error(formatApiErrorMessage(error));
        setAttributes(listCatalogAttributeRecords());
        setOrderedAttributeIds(listLinksForCategory(categoryId).map((l) => l.attributeId));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, categoryId, onOpenChange]);

  const attrLabel = useCallback(
    (id: ID) => {
      const a = attributes.find((x) => x.id === id);
      return a ? `${a.name} (${a.code})` : id;
    },
    [attributes],
  );

  const selectedAttrIdSet = useMemo(() => new Set(orderedAttributeIds), [orderedAttributeIds]);

  const filteredAttributes = useMemo(() => {
    const q = attrSearch.trim().toLowerCase();
    if (!q) return attributes;
    return attributes.filter(
      (a) => a.name.toLowerCase().includes(q) || a.code.toLowerCase().includes(q),
    );
  }, [attributes, attrSearch]);

  const filteredAttrSelectState = useMemo(() => {
    if (filteredAttributes.length === 0) return false as boolean | "indeterminate";
    const selectedCount = filteredAttributes.filter((a) => selectedAttrIdSet.has(a.id)).length;
    if (selectedCount === 0) return false;
    if (selectedCount === filteredAttributes.length) return true;
    return "indeterminate";
  }, [filteredAttributes, selectedAttrIdSet]);

  const toggleAttribute = useCallback((id: ID) => {
    setOrderedAttributeIds((ids) =>
      ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id],
    );
  }, []);

  const toggleSelectAllFilteredAttributes = useCallback(
    (checked: boolean | "indeterminate") => {
      const filteredIds = filteredAttributes.map((a) => a.id);
      if (filteredIds.length === 0) return;
      setOrderedAttributeIds((ids) => {
        if (checked === true) {
          const existing = new Set(ids);
          const toAdd = filteredIds.filter((id) => !existing.has(id));
          return [...ids, ...toAdd];
        }
        const remove = new Set(filteredIds);
        return ids.filter((id) => !remove.has(id));
      });
    },
    [filteredAttributes],
  );

  const move = (idx: number, dir: -1 | 1) => {
    setOrderedAttributeIds((ids) => {
      const next = [...ids];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return ids;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  const save = async () => {
    if (isSaving || !categoryId) return;
    setIsSaving(true);
    try {
      const result = await setCategoryAttributeLinksViaApi(categoryId, orderedAttributeIds);
      toast.success(result.message || "Category attributes updated.");
      await onSaved();
      onOpenChange(false);
    } catch (error) {
      toast.error(formatApiErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const titleName = categoryName?.trim() || "category";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Assign attributes — {titleName}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground -mt-1">
          Same as Categories edit: pick existing fields and link them to this product&apos;s category. Unassigned
          attributes can be added here without leaving the product form.
        </p>
        <div className="space-y-4 py-2 overflow-hidden flex flex-col min-h-0 flex-1">
          <div className="space-y-2 flex-1 min-h-0 flex flex-col">
            <Label className="text-xs text-muted-foreground">Fields for this category (order)</Label>
            {loading ? (
              <div className="flex items-center justify-center gap-2 rounded-lg border border-border/50 bg-muted/20 py-10 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading attributes…
              </div>
            ) : (
              <>
                <Popover
                  modal
                  open={attrPickerOpen}
                  onOpenChange={(next) => {
                    setAttrPickerOpen(next);
                    if (next) setAttrSearch("");
                  }}
                >
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className="glass-input h-10 w-full justify-between font-normal"
                    >
                      <span className={cn("truncate", orderedAttributeIds.length === 0 && "text-muted-foreground")}>
                        {orderedAttributeIds.length > 0
                          ? `${orderedAttributeIds.length} attribute${orderedAttributeIds.length > 1 ? "s" : ""} selected`
                          : "Search and select attributes…"}
                      </span>
                      <Search className="ml-2 h-4 w-4 shrink-0 opacity-70" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-[min(100vw-2rem,28rem)] p-0 z-[100]"
                    align="start"
                    sideOffset={6}
                  >
                    <div className="border-b border-border/50 p-2 space-y-2">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          autoFocus
                          className="h-9 pl-8"
                          placeholder="Search attributes by name or code…"
                          value={attrSearch}
                          onChange={(e) => setAttrSearch(e.target.value)}
                        />
                      </div>
                      {filteredAttributes.length > 0 ? (
                        <label className="flex items-center gap-2 px-0.5 text-xs font-medium cursor-pointer">
                          <Checkbox
                            checked={filteredAttrSelectState}
                            onCheckedChange={(v) => toggleSelectAllFilteredAttributes(v === true)}
                            aria-label={
                              filteredAttrSelectState === true ? "Deselect all attributes" : "Select all attributes"
                            }
                          />
                          <span className="text-muted-foreground">
                            {filteredAttrSelectState === true ? "Deselect all" : "Select all"}
                            {attrSearch.trim() ? " (filtered)" : ""}
                          </span>
                        </label>
                      ) : null}
                    </div>
                    <div className="max-h-[240px] overflow-y-auto p-1">
                      {filteredAttributes.length === 0 ? (
                        <p className="px-2 py-6 text-center text-sm text-muted-foreground">No attributes found.</p>
                      ) : (
                        filteredAttributes.map((a) => {
                          const selected = selectedAttrIdSet.has(a.id);
                          return (
                            <button
                              key={a.id}
                              type="button"
                              onClick={() => toggleAttribute(a.id)}
                              className={cn(
                                "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-muted/60",
                                selected && "bg-muted/40",
                              )}
                            >
                              <span
                                className={cn(
                                  "flex h-4 w-4 shrink-0 items-center justify-center rounded border",
                                  selected ? "border-primary/50 bg-primary/10" : "border-border",
                                )}
                              >
                                {selected ? <Check className="h-3 w-3 text-primary" /> : null}
                              </span>
                              <span className="flex-1 truncate">
                                {a.name} <span className="text-muted-foreground">({a.code})</span>
                              </span>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
                <ScrollArea className="h-[200px] rounded-lg border border-border/50 bg-muted/20">
                  <ul className="p-2 space-y-1">
                    {orderedAttributeIds.length === 0 ? (
                      <li className="px-2 py-6 text-center text-xs text-muted-foreground">
                        No attributes selected yet.
                      </li>
                    ) : null}
                    {orderedAttributeIds.map((id, idx) => (
                      <li
                        key={`${id}-${idx}`}
                        className="flex items-center gap-2 rounded-md px-2 py-1.5 bg-card border border-border/40 text-sm"
                      >
                        <span className="flex-1 truncate">{attrLabel(id)}</span>
                        <Button type="button" size="icon" variant="ghost" className="h-7 w-7" onClick={() => move(idx, -1)}>
                          <ArrowUp className="w-3.5 h-3.5" />
                        </Button>
                        <Button type="button" size="icon" variant="ghost" className="h-7 w-7" onClick={() => move(idx, 1)}>
                          <ArrowDown className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-destructive"
                          onClick={() =>
                            setOrderedAttributeIds((ids) => ids.filter((_, i) => i !== idx))
                          }
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                </ScrollArea>
              </>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="glass" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="premium" onClick={() => void save()} disabled={isSaving || loading || !categoryId}>
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving…
              </>
            ) : (
              "Save"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
